/**
 * Descriptor de Carteira de recebíveis — o fundo do ponto de vista do que ele carrega.
 *
 * O CRUD é o mais fino do cadastro, e de propósito: quase tudo que a carteira É vem da empresa que
 * a carrega (CNPJ, QSA, situação cadastral) ou do arquivo importado (posições e títulos). O que
 * sobra para editar à mão é o apelido, o originador e uma observação.
 *
 * As abas seguem a ordem da leitura, não a do processamento: primeiro DE QUEM é a carteira
 * (exposição por grupo econômico), depois o que há nela (posições e títulos), e só então as
 * leituras com evidência (achados). É a ordem da conclusão.
 */
import { Landmark } from 'lucide-react';
import { createElement } from 'react';
import { PainelAchados } from '@/components/crud/paineis/PainelAchados';
import { PainelComparacaoCarteira } from '@/components/crud/paineis/PainelComparacaoCarteira';
import { PainelPosicoesCarteira } from '@/components/crud/paineis/PainelPosicoesCarteira';
import { PainelRelatorioCarteira } from '@/components/crud/paineis/PainelRelatorioCarteira';
import type { CarteiraCad } from '@/lib/api';
import { definirRecurso } from '@/lib/recursos/tipos';

export const carteira = definirRecurso<CarteiraCad>({
  chave: 'carteiras',
  endpoint: '/cadastro/carteiras',
  ownerTipo: 'carteira',
  titulo: 'Carteiras',
  tituloSingular: 'carteira',
  descricao:
    'Carteiras de recebíveis — o estoque de um fundo, título a título, com o grupo econômico por trás.',
  icone: Landmark,
  capacidades: { podeEditar: true, podeExcluir: true },
  buscaPlaceholder: 'Buscar pelo apelido, razão social ou CNPJ do fundo',
  // O apelido quando há; senão a razão social da empresa. Uma carteira sem nome na tela seria uma
  // linha que o operador não consegue identificar.
  rotuloItem: (c) => c.nome || c.empresa_razao_social || 'Carteira',

  colunas: [
    { chave: 'nome', rotulo: 'Carteira' },
    { chave: 'empresa_razao_social', rotulo: 'Fundo (Receita)', ocultarEm: 'md' },
    { chave: 'empresa_cnpj', rotulo: 'CNPJ', formato: 'cnpj', mono: true, ocultarEm: 'sm' },
    { chave: 'posicoes', rotulo: 'Posições', alinhamento: 'direita', mono: true },
    { chave: 'ultima_referencia', rotulo: 'Última', formato: 'data', mono: true },
  ],

  // Criar à mão é o caminho raro: a carteira nasce da importação do estoque, que já cria a empresa
  // do fundo pelo CNPJ. Aqui é para o caso de o operador querer preparar a ficha antes do arquivo.
  camposCriar: [
    {
      chave: 'empresa_id',
      rotulo: 'Fundo (empresa já cadastrada)',
      tipo: 'referencia',
      refEndpoint: '/cadastro/empresas',
      refRotulo: 'razao_social',
      obrigatorio: true,
      ajuda: 'O fundo É uma empresa: é dela que vêm CNPJ, quadro societário e situação cadastral.',
    },
    { chave: 'nome', rotulo: 'Apelido', tipo: 'texto' },
  ],

  // Ingestão do estoque: sobe o arquivo, a IA sugere o mapa coluna→chave contra o vocabulário do
  // domínio, o operador revisa, e a assinatura das colunas vira molde para o próximo mês.
  importarPlanilha: {
    rotulo: 'Importar estoque',
    endpoint: '/cadastro/ingestao/carteiras',
    descricao:
      'Suba a posição da carteira (CSV ou Excel). O formato não precisa ser o do regulador: você ' +
      'confirma qual coluna alimenta cada campo, e o layout vira molde para o próximo arquivo.',
  },

  gruposEdicao: [
    {
      id: 'identificacao',
      titulo: 'Identificação',
      descricao:
        'O nome oficial e o CNPJ moram na EMPRESA do fundo — aqui fica o apelido de uso, para ' +
        'quando o da Receita é impronunciável.',
      campos: [
        { chave: 'nome', rotulo: 'Apelido', tipo: 'texto' },
        { chave: 'empresa_razao_social', rotulo: 'Razão social (Receita)', tipo: 'leitura' },
        { chave: 'empresa_cnpj', rotulo: 'CNPJ', tipo: 'leitura', formato: 'cnpj' },
        {
          chave: 'originador_empresa_id',
          rotulo: 'Originador',
          tipo: 'referencia',
          refEndpoint: '/cadastro/empresas',
          refRotulo: 'razao_social',
          ajuda: 'Quem originou os recebíveis — costuma ser o nó que explica a carteira inteira.',
        },
        { chave: 'observacoes', rotulo: 'Observações', tipo: 'texto-longo' },
      ],
    },
    {
      id: 'sistema',
      titulo: 'Sistema',
      campos: [
        { chave: 'ativo', rotulo: 'Ativa', tipo: 'booleano' },
        { chave: 'created_at', rotulo: 'Criada em', tipo: 'leitura' },
      ],
    },
  ],

  abas: [
    // A primeira aba é o RELATÓRIO: quem abre uma carteira quer a leitura pronta, não a matéria
    // -prima. As abas seguintes existem para conferir o que ele afirma.
    {
      id: 'relatorio',
      rotulo: 'Relatório',
      paineis: [
        {
          tipo: 'custom',
          id: 'carteira-relatorio',
          titulo: 'Relatório',
          render: ({ item }) => createElement(PainelRelatorioCarteira, { carteiraId: item.id }),
        },
      ],
    },
    {
      id: 'posicoes',
      rotulo: 'Posições e títulos',
      paineis: [
        {
          tipo: 'custom',
          id: 'carteira-posicoes',
          titulo: 'Posições e títulos',
          render: ({ item }) => createElement(PainelPosicoesCarteira, { carteiraId: item.id }),
        },
      ],
    },
    {
      id: 'mudanca',
      rotulo: 'O que mudou',
      paineis: [
        {
          tipo: 'custom',
          id: 'carteira-comparacao',
          titulo: 'O que mudou',
          render: ({ item }) => createElement(PainelComparacaoCarteira, { carteiraId: item.id }),
        },
      ],
    },
    {
      id: 'achados',
      rotulo: 'O que a carteira diz',
      paineis: [
        {
          tipo: 'custom',
          id: 'carteira-achados',
          titulo: 'O que a carteira diz',
          render: ({ item }) =>
            createElement(PainelAchados, {
              dominio: 'credito',
              alvo: { tipo: 'carteira', id: item.id },
            }),
        },
      ],
    },
    { id: 'dados', rotulo: 'Dados da carteira', grupos: ['identificacao', 'sistema'] },
    { id: 'documentos', rotulo: 'Documentos', paineis: [{ tipo: 'documentos' }] },
  ],
});
